import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { saveSchadenKICredentials } from "@/services/schaden-ki/saveSchadenKICredentials";
import { getSchadenKICredentials } from "@/services/schaden-ki/getSchadenKICredentials";
import SchadenKIStore from "@/stores/SchadenKIStore";

export function useSchadenKICredentials() {
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const queryClient = useQueryClient();

  // Use the Schaden KI store
  const localApiKey = SchadenKIStore(state => state.localApiKey);
  const localApiUrl = SchadenKIStore(state => state.localApiUrl);
  const hasChanges = SchadenKIStore(state => state.hasChanges);
  const setLocalApiKey = SchadenKIStore(state => state.setLocalApiKey);
  const setLocalApiUrl = SchadenKIStore(state => state.setLocalApiUrl);
  const setOriginalApiKey = SchadenKIStore(state => state.setOriginalApiKey);
  const setOriginalApiUrl = SchadenKIStore(state => state.setOriginalApiUrl);

  const { data: credentials, isLoading } = useQuery({
    queryKey: ["schadenKICredentials"],
    queryFn: getSchadenKICredentials,
  });

  // Sync store with query data when it changes
  useEffect(() => {
    if (credentials) {
      setOriginalApiKey(credentials.apiKey);
      setOriginalApiUrl(credentials.apiUrl);
    }
  }, [credentials, setOriginalApiKey, setOriginalApiUrl]);

  const { mutate: saveCredentialsMutation, isPending: isSaving } = useMutation({
    mutationFn: () => saveSchadenKICredentials(localApiKey, localApiUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schadenKICredentials"] });
      // Reset changes after successful save
      setOriginalApiKey(localApiKey);
      setOriginalApiUrl(localApiUrl);
    },
  });

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalApiKey(e.target.value.trim());
  };

  const handleApiUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalApiUrl(e.target.value.trim());
  };

  const toggleApiKeyVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  const handleSaveCredentials = () => {
    // Ensure no leading/trailing spaces before saving
    setLocalApiKey(localApiKey.trim());
    setLocalApiUrl(localApiUrl.trim());
    saveCredentialsMutation();
  };

  return {
    apiKey: localApiKey,
    apiUrl: localApiUrl,
    isLoading: isLoading || isSaving,
    showApiKey,
    hasChanges,
    handleApiKeyChange,
    handleApiUrlChange,
    toggleApiKeyVisibility,
    handleSaveCredentials,
  };
}

